/**
 * 职责：内存事件源，按序投递 events 实现 Source 接口。
 * 链路位置：历史回放、测试、AgentSession 只读时间线。
 * 当前裁剪范围：可选 delayMs 模拟流式；不含 SSE 续传。
 */
import type { LangchainAgentEvent } from '../types/events';
import type { ReplayOptions } from '../core/replay';
import type { Source, SourceHandler } from './types';

export interface ReplaySourceOptions extends ReplayOptions {
  /** 事件间隔毫秒；默认 0 立即投递 */
  delayMs?: number;
}

function sleep(ms: number, signal: AbortSignal): Promise<void> {
  if (ms <= 0) return Promise.resolve();
  return new Promise((resolve) => {
    const timer = setTimeout(resolve, ms);
    signal.addEventListener(
      'abort',
      () => {
        clearTimeout(timer);
        resolve();
      },
      { once: true },
    );
  });
}

export function createReplaySource(
  events: LangchainAgentEvent[],
  opts?: ReplaySourceOptions,
): Source {
  const delayMs = opts?.delayMs ?? 0;

  return {
    async start(handler: SourceHandler, signal: AbortSignal): Promise<void> {
      for (const event of events) {
        if (signal.aborted) break;
        handler(event);
        if (delayMs > 0) {
          await sleep(delayMs, signal);
        }
      }
    },
  };
}
